import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { isApiError, toPlayerMessage } from '@/shared/api';
import { isFresh, singleFlight } from '@/shared/lib';
import { getInitData } from '@/shared/lib/telegram';

import {
  fetchMySummary,
  loginWithTelegram,
  refreshSubscription as refreshSubscriptionRequest,
  updateDisplayName as updateDisplayNameRequest,
} from '../api/session.api';

import type { SessionStatus, UserProfile } from './types';

/**
 * Текущий игрок целиком: токен, профиль и его счётчики.
 *
 * Счётчики держит этот же срез, а не соседний: они приходят одним ответом
 * вместе с профилем, а отдельный срез не смог бы импортировать сессию —
 * соседи в FSD друг друга не видят. Шапка и карточки читают отсюда, а
 * не из среза Прогнозов, где баланс Фанкоинов лежал по historical reasons.
 */
interface SessionState {
  accessToken: string | null;
  user: UserProfile | null;
  status: SessionStatus;
  error: string | null;
  /** Первый вход этого игрока — экран онбординга показывается один раз. */
  isNewUser: boolean;

  funcoinBalance: number;
  weeklyPoints: number;
  monthlyPoints: number;
  freebetsEarned: number;
  /** `false` — сезона сейчас нет, и нули означают именно это. */
  hasActivePeriod: boolean;
  summaryLoadedAt: number | null;

  loadSummary: (options?: { force?: boolean }) => Promise<void>;
  /**
   * Баланс после подтверждения Прогноза: сервер возвращает его уже
   * посчитанным, перезапрашивать сводку незачем.
   */
  applyFuncoinBalance: (balance: number) => void;
  /**
   * Перепроверить подписку на канал. Зовётся только у неподписанных —
   * см. `refresh-app-data`.
   */
  recheckSubscription: () => Promise<void>;
  changeDisplayName: (displayName: string) => Promise<void>;
  logout: () => void;
}

const EMPTY_COUNTERS = {
  funcoinBalance: 0,
  weeklyPoints: 0,
  monthlyPoints: 0,
  freebetsEarned: 0,
  hasActivePeriod: false,
  summaryLoadedAt: null,
} as const;

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      status: 'idle',
      error: null,
      isNewUser: false,
      ...EMPTY_COUNTERS,

      loadSummary: singleFlight(
        async (options?: { force?: boolean }): Promise<void> => {
          if (!options?.force && isFresh(get().summaryLoadedAt)) return;

          const summary = await fetchMySummary();
          set({
            user: summary.profile,
            funcoinBalance: summary.funcoinBalance,
            weeklyPoints: summary.weeklyPoints,
            monthlyPoints: summary.monthlyPoints,
            freebetsEarned: summary.freebetsEarned,
            hasActivePeriod: summary.hasActivePeriod,
            summaryLoadedAt: Date.now(),
            status: 'authenticated',
            error: null,
          });
        },
      ),

      /**
       * Флаг в сторе правится точечно, а не перезагрузкой сводки: сводка
       * уже уехала параллельно этим же обновлением, и второй запрос
       * ничего бы не добавил. Ачивку за подписку сервер засчитывает сам.
       */
      recheckSubscription: async () => {
        const status = await refreshSubscriptionRequest();
        const user = get().user;
        if (!user || user.isSubscribed === status.isSubscribed) return;
        set({ user: { ...user, isSubscribed: status.isSubscribed } });
      },

      applyFuncoinBalance: (balance) => {
        set({ funcoinBalance: balance });
      },

      changeDisplayName: async (displayName) => {
        const user = await updateDisplayNameRequest(displayName);
        set({ user });
      },

      logout: () => {
        set({
          accessToken: null,
          user: null,
          status: 'idle',
          error: null,
          ...EMPTY_COUNTERS,
        });
      },
    }),
    {
      name: 'betboom.session',
      // В localStorage кладём только токен: профиль и счётчики всегда тянем
      // заново, иначе игрок увидит вчерашние Очки и статус подписки.
      partialize: (state) => ({ accessToken: state.accessToken }),
    },
  ),
);

/**
 * Вход по initData.
 *
 * Single-flight: параллельные 401 не должны превратиться в очередь входов —
 * каждый лишний вход перезаписывает токен и роняет соседние запросы.
 */
let loginInFlight: Promise<string | null> | null = null;

export function authenticate(): Promise<string | null> {
  loginInFlight ??= runLogin().finally(() => {
    loginInFlight = null;
  });
  return loginInFlight;
}

async function runLogin(): Promise<string | null> {
  useSessionStore.setState({ status: 'authenticating', error: null });

  try {
    const { accessToken, isNewUser } = await loginWithTelegram(getInitData());
    useSessionStore.setState({ accessToken, isNewUser });
    return accessToken;
  } catch (error) {
    useSessionStore.setState({
      accessToken: null,
      user: null,
      status: 'error',
      error: toPlayerMessage(error),
    });
    return null;
  }
}

/**
 * Поднимает сессию при старте приложения: токен из localStorage проверяется
 * запросом профиля, протухший меняется на новый прозрачно для UI
 * (перелогин на 401 делает http-клиент).
 *
 * Тоже single-flight: StrictMode монтирует эффекты дважды, и без этого
 * второй вызов перезаписывал бы статус уже упавшего входа обратно
 * на `authenticating` — приложение навсегда зависало на спиннере.
 */
let bootstrapInFlight: Promise<void> | null = null;

export function bootstrapSession(): Promise<void> {
  bootstrapInFlight ??= runBootstrap().finally(() => {
    bootstrapInFlight = null;
  });
  return bootstrapInFlight;
}

async function runBootstrap(): Promise<void> {
  useSessionStore.setState({ status: 'authenticating', error: null });

  const { accessToken } = useSessionStore.getState();
  if (!accessToken && !(await authenticate())) return;

  try {
    await useSessionStore.getState().loadSummary({ force: true });
  } catch (error) {
    // Токен подписан верно, но игрока за ним больше нет: запись удалили
    // из базы, а токен остался в localStorage. Для 401 перелогин делает
    // http-клиент, а тут приходит 404 — и без этой ветки приложение
    // навсегда застревало на «Не удалось войти»: сам по себе токен
    // не протухнет ещё час, а очистить хранилище изнутри Telegram нечем.
    if (isApiError(error) && error.code === 'users.not_found') {
      useSessionStore.setState({ accessToken: null });
      if (await authenticate()) {
        try {
          await useSessionStore.getState().loadSummary({ force: true });
          return;
        } catch (retryError) {
          useSessionStore.setState({
            status: 'error',
            error: toPlayerMessage(retryError),
          });
          return;
        }
      }
      return;
    }

    useSessionStore.setState({
      status: 'error',
      error: toPlayerMessage(error),
    });
  }
}

export const selectIsAuthenticated = (state: SessionState): boolean =>
  state.status === 'authenticated' && state.user !== null;
